import { SideNav } from "@design-system/SideNav/SideNav";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import { SideNavSubItem } from "@design-system/SideNav/SideNavSubItem";
import { Tag } from "@design-system/Tag/Tag";
import {
    AccountsIcon,
    BillPayIcon,
    CalendarIcon,
    ChartOfAccountsIcon,
    ClassIcon,
    ClockIcon,
    DirectoryIcon,
    FolderIcon,
    GraphIcon,
    GrowthIcon,
    IntercompanyIcon,
    InvoicingIcon,
    OwnershipIcon,
    ReconciledIcon,
    RobotIcon,
    RulesIcon,
    SkillsIcon,
    TransactionsIcon,
} from "@icons";
import {
    PlaygroundEntityIcon,
    PlaygroundHeader,
    PlaygroundHelpButton,
    PlaygroundTopRows,
    usePlaygroundNav,
    type SideNavCompositionProps,
} from "./SideNavPlaygroundParts";

/**
 * Workspace nav per the spec's Migration map, plus the unshipped Skills row.
 * Accounting starts collapsed; Transactions and the first entity start expanded.
 */
export const WorkspaceSideNav = ({
    className,
    forcedRowState,
}: SideNavCompositionProps) => {
    const nav = usePlaygroundNav({
        activeId: "transactions",
        closedSections: ["Accounting"],
        expandedRows: ["transactions", "entity:acme"],
    });

    return (
        <SideNav
            aria-label="Main"
            className={className}
            header={<PlaygroundHeader name="Acme Holdings" shape="square" />}
            top={
                <>
                    <PlaygroundTopRows nav={nav} taskCount={3} />
                    <SideNavItem
                        {...nav.link("activity")}
                        icon={<ClockIcon />}
                        label="Activity"
                    />
                </>
            }
            footer={
                <>
                    <PlaygroundHelpButton />
                    <Tag size="sm">Trial · 14 days left</Tag>
                </>
            }
        >
            <SideNavSection
                {...nav.section("Data")}
                data-forced-state={forcedRowState}
            >
                <SideNavItem
                    {...nav.link("accounts")}
                    icon={<AccountsIcon />}
                    label="Accounts"
                    data-forced-state={forcedRowState}
                />
                <SideNavItem
                    {...nav.link("transactions")}
                    {...nav.expansion("transactions")}
                    icon={<TransactionsIcon />}
                    label="Transactions"
                >
                    <SideNavSubItem
                        {...nav.link("categories")}
                        label="Categories"
                    />
                </SideNavItem>
                <SideNavItem
                    {...nav.link("documents")}
                    {...nav.expansion("documents")}
                    icon={<FolderIcon />}
                    label="Documents"
                >
                    <SideNavSubItem
                        {...nav.link("document-categories")}
                        label="Document categories"
                    />
                </SideNavItem>
                <SideNavItem
                    {...nav.link("ownership")}
                    icon={<OwnershipIcon />}
                    label="Ownership"
                />
            </SideNavSection>
            <SideNavSection {...nav.section("Reports")}>
                <SideNavItem
                    {...nav.link("group:acme")}
                    icon={<PlaygroundEntityIcon name="Acme Group" />}
                    label="Acme Group"
                />
                <SideNavItem
                    {...nav.link("entity:acme")}
                    {...nav.expansion("entity:acme")}
                    icon={<PlaygroundEntityIcon name="Acme Manufacturing" />}
                    label="Acme Manufacturing International LLC"
                >
                    <SideNavSubItem {...nav.link("acme:pl")} label="P&L" />
                    <SideNavSubItem
                        {...nav.link("acme:balance-sheet")}
                        label="Balance Sheet"
                    />
                    <SideNavSubItem
                        {...nav.link("acme:reports")}
                        label="All reports"
                    />
                </SideNavItem>
                <SideNavItem
                    {...nav.link("insights")}
                    icon={<GraphIcon />}
                    label="Insights"
                />
                <SideNavItem
                    {...nav.link("reports")}
                    icon={<GrowthIcon />}
                    label="All reports"
                />
            </SideNavSection>
            <SideNavSection {...nav.section("Accounting")}>
                <SideNavItem
                    {...nav.link("chart-of-accounts")}
                    icon={<ChartOfAccountsIcon />}
                    label="Chart of Accounts"
                />
                <SideNavItem
                    {...nav.link("counterparties")}
                    icon={<DirectoryIcon />}
                    label="Counterparties"
                />
                <SideNavItem
                    {...nav.link("classes")}
                    icon={<ClassIcon />}
                    label="Classes"
                />
                <SideNavItem
                    {...nav.link("reconciliation")}
                    icon={<ReconciledIcon />}
                    label="Reconciliation"
                />
                <SideNavItem
                    {...nav.link("recognition")}
                    icon={<CalendarIcon />}
                    label="Recognition schedules"
                />
                <SideNavItem
                    {...nav.link("intercompany")}
                    icon={<IntercompanyIcon />}
                    label="Intercompany"
                />
            </SideNavSection>
            <SideNavSection {...nav.section("Automation")}>
                <SideNavItem
                    {...nav.link("rules")}
                    icon={<RulesIcon />}
                    label="Rules"
                />
                <SideNavItem
                    {...nav.link("connectors")}
                    icon={<RobotIcon />}
                    label="Connectors"
                />
                <SideNavItem
                    {...nav.link("skills")}
                    icon={<SkillsIcon />}
                    label="Skills"
                />
            </SideNavSection>
            <SideNavSection {...nav.section("Payments")}>
                <SideNavItem
                    {...nav.link("invoicing")}
                    {...nav.expansion("invoicing")}
                    icon={<InvoicingIcon />}
                    label="Invoicing"
                >
                    <SideNavSubItem
                        {...nav.link("invoicing:inbox")}
                        label="Inbox"
                    />
                    <SideNavSubItem {...nav.link("items")} label="Items" />
                    <SideNavSubItem
                        {...nav.link("templates")}
                        label="Templates"
                    />
                </SideNavItem>
                <SideNavItem
                    {...nav.link("bill-pay")}
                    {...nav.expansion("bill-pay")}
                    icon={<BillPayIcon />}
                    label="Bill pay"
                >
                    <SideNavSubItem
                        {...nav.link("bill-pay:inbox")}
                        label="Inbox"
                    />
                </SideNavItem>
            </SideNavSection>
        </SideNav>
    );
};
